import { gsap } from 'gsap';

const teamCardsArr = [...document.querySelectorAll('.team-card')];

if (teamCardsArr.length) {
  const MODAL_VIEWPORT_OFFSET = 20;
  const MODAL_BOUNDS_BREAKPOINT = 1024;
  const MODAL_CLOSE_Z_INDEX_DELAY = 420;
  const DEFAULT_MODAL_ANCHOR_X = 0.82;
  const DEFAULT_MODAL_ANCHOR_Y = 0.18;
  const modalLayerTimeouts = new WeakMap();
  const modalParallaxSetters = new WeakMap();
  let activeParallaxCard = null;
  let parallaxRafId = null;
  let isParallaxListenersActive = false;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const clearModalLayerTimeout = (teamCard) => {
    const timeoutId = modalLayerTimeouts.get(teamCard);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      modalLayerTimeouts.delete(teamCard);
    }
  };

  const enableModalLayer = (teamCard) => {
    clearModalLayerTimeout(teamCard);
    teamCard.classList.add('is-modal-z-active');
  };

  const disableModalLayer = (teamCard) => {
    clearModalLayerTimeout(teamCard);

    const timeoutId = window.setTimeout(() => {
      teamCard.classList.remove('is-modal-z-active');
      modalLayerTimeouts.delete(teamCard);
    }, MODAL_CLOSE_Z_INDEX_DELAY);

    modalLayerTimeouts.set(teamCard, timeoutId);
  };

  const getModalParallaxTarget = (teamCard) => teamCard?.querySelector('.team-card__modal-parallax-wr') || null;

  const resetModalParallax = (teamCard) => {
    const parallaxTarget = getModalParallaxTarget(teamCard);

    if (!parallaxTarget) {
      return;
    }

    const quickToY = modalParallaxSetters.get(teamCard) || gsap.quickTo(parallaxTarget, 'y', {
      duration: 0.8,
      ease: 'power3.out',
      overwrite: 'auto',
    });

    modalParallaxSetters.set(teamCard, quickToY);
    quickToY(0);
  };

  const updateActiveModalParallax = () => {
    parallaxRafId = null;

    if (!activeParallaxCard || window.innerWidth <= MODAL_BOUNDS_BREAKPOINT - 1 || !activeParallaxCard.classList.contains('is-show-modal')) {
      return;
    }

    const modalWrEl = activeParallaxCard.querySelector('.team-card__modal-wr');

    if (!modalWrEl) {
      return;
    }

    const modalRect = modalWrEl.getBoundingClientRect();

    if (modalRect.bottom <= 0 || modalRect.top >= window.innerHeight || modalRect.right <= 0 || modalRect.left >= window.innerWidth) {
      closeTeamCard(activeParallaxCard);
      return;
    }

    const parallaxTarget = getModalParallaxTarget(activeParallaxCard);

    if (!parallaxTarget) {
      return;
    }

    const quickToY = modalParallaxSetters.get(activeParallaxCard) || gsap.quickTo(parallaxTarget, 'y', {
      duration: 0.8,
      ease: 'power3.out',
      overwrite: 'auto',
    });

    modalParallaxSetters.set(activeParallaxCard, quickToY);

    if (!quickToY) {
      return;
    }

    const cardRect = activeParallaxCard.getBoundingClientRect();
    const viewportCenter = window.innerHeight * 0.5;
    const cardCenter = cardRect.top + (cardRect.height * 0.5);
    const distanceRatio = (viewportCenter - cardCenter) / window.innerHeight;
    const parallaxY = clamp(distanceRatio * 108, -48, 48);

    quickToY(parallaxY);
  };

  const requestModalParallaxUpdate = () => {
    if (parallaxRafId !== null || !activeParallaxCard) {
      return;
    }

    parallaxRafId = window.requestAnimationFrame(updateActiveModalParallax);
  };

  const handleParallaxScroll = () => {
    requestModalParallaxUpdate();
  };

  const updateParallaxListenersState = () => {
    const shouldBeActive = Boolean(
        activeParallaxCard
        && activeParallaxCard.classList.contains('is-show-modal')
        && window.innerWidth >= MODAL_BOUNDS_BREAKPOINT,
    );

    if (shouldBeActive === isParallaxListenersActive) {
      return;
    }

    if (shouldBeActive) {
      window.addEventListener('scroll', handleParallaxScroll, { passive: true });

      if (window.lenis && typeof window.lenis.on === 'function') {
        window.lenis.on('scroll', handleParallaxScroll);
      }

      isParallaxListenersActive = true;
      return;
    }

    window.removeEventListener('scroll', handleParallaxScroll);

    if (window.lenis && typeof window.lenis.off === 'function') {
      window.lenis.off('scroll', handleParallaxScroll);
    }

    if (parallaxRafId !== null) {
      window.cancelAnimationFrame(parallaxRafId);
      parallaxRafId = null;
    }

    isParallaxListenersActive = false;
  };

  const getTeamSwiperInstance = (teamCard) => {
    const swiperEl = teamCard.closest('.teams-swiper-wr')?.querySelector('.swiper');

    return swiperEl?.swiper || null;
  };

  const pauseTeamSwiper = (teamCard) => {
    const swiperInstance = getTeamSwiperInstance(teamCard);

    if (!swiperInstance) {
      return;
    }

    swiperInstance.allowTouchMove = false;

    if (swiperInstance.autoplay && typeof swiperInstance.autoplay.stop === 'function') {
      swiperInstance.autoplay.stop();
    }
  };

  const resumeTeamSwiper = (teamCard) => {
    const swiperInstance = getTeamSwiperInstance(teamCard);

    if (!swiperInstance) {
      return;
    }

    swiperInstance.allowTouchMove = true;

    if (swiperInstance.autoplay && typeof swiperInstance.autoplay.start === 'function') {
      swiperInstance.autoplay.start();
    }
  };

  const throttle = (callback, wait = 120) => {
    let isThrottled = false;
    let pendingArgs = null;

    const run = (...args) => {
      if (isThrottled) {
        pendingArgs = args;
        return;
      }

      callback(...args);
      isThrottled = true;

      window.setTimeout(() => {
        isThrottled = false;

        if (pendingArgs) {
          const nextArgs = pendingArgs;
          pendingArgs = null;
          run(...nextArgs);
        }
      }, wait);
    };

    return run;
  };

  const updateTeamModalBoundsState = () => {
    teamCardsArr.forEach((teamCard) => {
      const modalWrEl = teamCard.querySelector('.team-card__modal-wr');

      if (!modalWrEl) {
        return;
      }

      modalWrEl.classList.remove('is-out-left', 'is-out-right', 'is-out-bounds', 'is-align-right', 'is-align-bottom');

      if (window.innerWidth <= MODAL_BOUNDS_BREAKPOINT - 1) {
        const infoBtnEl = teamCard.querySelector('.team-card__info-btn');
        const cardRect = teamCard.getBoundingClientRect();
        const modalHeight = modalWrEl.offsetHeight;

        if (infoBtnEl && infoBtnEl.offsetHeight > 0 && modalHeight > 0) {
          const infoBtnRect = infoBtnEl.getBoundingClientRect();
          const modalTop = ((infoBtnRect.bottom - cardRect.top) - modalHeight) + 4;

          modalWrEl.style.setProperty('--team-modal-mobile-top', `${modalTop}px`);
        } else {
          modalWrEl.style.removeProperty('--team-modal-mobile-top');
        }

        modalWrEl.style.removeProperty('--team-modal-left');
        modalWrEl.style.removeProperty('--team-modal-top');
        modalWrEl.style.removeProperty('--team-modal-origin-x');
        modalWrEl.style.removeProperty('--team-modal-origin-y');
        modalWrEl.style.removeProperty('--team-modal-enter-x');
        modalWrEl.style.removeProperty('--team-modal-enter-y');
        return;
      }

      modalWrEl.style.removeProperty('--team-modal-mobile-top');

      const cardRect = teamCard.getBoundingClientRect();
      const modalWidth = modalWrEl.offsetWidth;
      const modalHeight = modalWrEl.offsetHeight;
      const anchorXRatio = Number(teamCard.dataset.modalAnchorX || DEFAULT_MODAL_ANCHOR_X);
      const anchorYRatio = Number(teamCard.dataset.modalAnchorY || DEFAULT_MODAL_ANCHOR_Y);
      const anchorClientX = cardRect.left + (cardRect.width * anchorXRatio);
      const anchorClientY = cardRect.top + (cardRect.height * anchorYRatio);

      const horizontalGap = 18;
      const verticalAnchorOffset = Math.min(44, modalHeight * 0.22);
      const preferredViewportLeft = anchorClientX + horizontalGap;
      const preferredViewportTop = anchorClientY - verticalAnchorOffset;
      const maxViewportLeft = Math.max(MODAL_VIEWPORT_OFFSET, window.innerWidth - MODAL_VIEWPORT_OFFSET - modalWidth);
      const maxViewportTop = Math.max(MODAL_VIEWPORT_OFFSET, window.innerHeight - MODAL_VIEWPORT_OFFSET - modalHeight);
      const canPlaceToRight = preferredViewportLeft + modalWidth <= window.innerWidth - MODAL_VIEWPORT_OFFSET;
      const canPlaceByY = preferredViewportTop + modalHeight <= window.innerHeight - MODAL_VIEWPORT_OFFSET;

      let finalViewportLeft = preferredViewportLeft;
      let finalViewportTop = preferredViewportTop;
      let isAlignedRight = false;
      let isAlignedBottom = false;

      if (!canPlaceToRight) {
        finalViewportLeft = maxViewportLeft;
        isAlignedRight = true;
      } else {
        finalViewportLeft = clamp(preferredViewportLeft, MODAL_VIEWPORT_OFFSET, maxViewportLeft);
      }

      if (!canPlaceByY) {
        finalViewportTop = maxViewportTop;
        isAlignedBottom = true;
      } else {
        finalViewportTop = clamp(preferredViewportTop, MODAL_VIEWPORT_OFFSET, maxViewportTop);
      }

      const modalLeft = finalViewportLeft - cardRect.left;
      const modalTop = finalViewportTop - cardRect.top;
      const originX = clamp(((anchorClientX - finalViewportLeft) / modalWidth) * 100, 12, 88);
      const originY = clamp(((anchorClientY - finalViewportTop) / modalHeight) * 100, 12, 88);
      const enterX = isAlignedRight ? 10 : -14;
      const enterY = isAlignedBottom ? 10 : -10;
      const isOutLeft = finalViewportLeft <= MODAL_VIEWPORT_OFFSET;
      const isOutRight = !canPlaceToRight;

      if (isOutLeft) {
        modalWrEl.classList.add('is-out-left');
      }

      if (isOutRight) {
        modalWrEl.classList.add('is-out-right');
      }

      if (isOutLeft || isOutRight) {
        modalWrEl.classList.add('is-out-bounds');
      }

      if (isAlignedRight) {
        modalWrEl.classList.add('is-align-right');
      }

      if (isAlignedBottom) {
        modalWrEl.classList.add('is-align-bottom');
      }

      modalWrEl.style.setProperty('--team-modal-left', `${modalLeft}px`);
      modalWrEl.style.setProperty('--team-modal-top', `${modalTop}px`);
      modalWrEl.style.setProperty('--team-modal-origin-x', `${originX}%`);
      modalWrEl.style.setProperty('--team-modal-origin-y', `${originY}%`);
      modalWrEl.style.setProperty('--team-modal-enter-x', `${enterX.toFixed(2)}px`);
      modalWrEl.style.setProperty('--team-modal-enter-y', `${enterY.toFixed(2)}px`);
    });
  };

  const closeTeamCard = (teamCard) => {
    if (!teamCard) {
      return;
    }

    if (activeParallaxCard === teamCard) {
      activeParallaxCard = null;
    }

    resetModalParallax(teamCard);
    updateParallaxListenersState();
    teamCard.classList.remove('is-show-modal');
    teamCard.classList.remove('cursor-native');
    disableModalLayer(teamCard);
    resumeTeamSwiper(teamCard);
  };

  const closeAllTeamCards = (excludeCard = null) => {
    teamCardsArr.forEach((teamCard) => {
      if (teamCard !== excludeCard) {
        closeTeamCard(teamCard);
      }
    });
  };

  document.addEventListener('click', (event) => {
    const modalEl = event.target.closest('.team-card__modal-wr');

    if (modalEl) {
      const closeBtnEl = event.target.closest('.team-card-modal__close-btn');

      if (closeBtnEl) {
        const teamCard = closeBtnEl.closest('.team-card');

        closeTeamCard(teamCard);
      }

      return;
    }

    const teamCard = event.target.closest('.team-card');

    if (!teamCard) {
      closeAllTeamCards();
      return;
    }

    const hasModal = teamCard.querySelector('.team-card__modal-wr');

    if (!hasModal) {
      return;
    }

    const teamCardRect = teamCard.getBoundingClientRect();

    if (teamCardRect.width > 0 && teamCardRect.height > 0) {
      const clickRatioX = clamp((event.clientX - teamCardRect.left) / teamCardRect.width, 0.08, 0.92);
      const clickRatioY = clamp((event.clientY - teamCardRect.top) / teamCardRect.height, 0.08, 0.92);

      teamCard.dataset.modalAnchorX = clickRatioX.toFixed(4);
      teamCard.dataset.modalAnchorY = clickRatioY.toFixed(4);
    }

    const isShown = teamCard.classList.contains('is-show-modal');

    closeAllTeamCards(teamCard);

    if (isShown) {
      closeTeamCard(teamCard);
      return;
    }

    enableModalLayer(teamCard);
    teamCard.classList.add('is-show-modal');
    teamCard.classList.add('cursor-native');
    pauseTeamSwiper(teamCard);
    activeParallaxCard = teamCard;
    updateTeamModalBoundsState();
    updateParallaxListenersState();
    requestModalParallaxUpdate();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllTeamCards();
    }
  });

  const handleResize = throttle(() => {
    updateTeamModalBoundsState();
    updateParallaxListenersState();
    requestModalParallaxUpdate();
  }, 140);

  updateTeamModalBoundsState();
  window.addEventListener('resize', handleResize);
}
